import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../services/api';
import { PlatformIcon, PLATFORM_COLORS } from '../components/shared/PlatformIcon';
import toast from 'react-hot-toast';

type PostStatus = 'all' | 'draft' | 'scheduled' | 'published' | 'failed' | 'publishing';

const STATUS_CONFIG: Record<string, { label: string; cls: string }> = {
  draft:      { label: 'Rascunho',   cls: 'bg-slate-700 text-slate-300' },
  scheduled:  { label: 'Agendado',   cls: 'bg-blue-900/60 text-blue-300' },
  publishing: { label: 'Publicando', cls: 'bg-amber-900/60 text-amber-300' },
  published:  { label: 'Publicado',  cls: 'bg-emerald-900/60 text-emerald-300' },
  failed:     { label: 'Falhou',     cls: 'bg-red-900/60 text-red-300' },
  partial:    { label: 'Parcial',    cls: 'bg-orange-900/60 text-orange-300' },
  cancelled:  { label: 'Cancelado',  cls: 'bg-slate-800 text-slate-500' },
};

const TABS: { id: PostStatus; label: string }[] = [
  { id: 'all',       label: 'Todos' },
  { id: 'scheduled', label: 'Agendados' },
  { id: 'published', label: 'Publicados' },
  { id: 'draft',     label: 'Rascunhos' },
  { id: 'failed',    label: 'Falhas' },
];

function formatDate(iso?: string) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
}

function toLocalDatetimeValue(iso?: string) {
  if (!iso) {
    const d = new Date(Date.now() + 60 * 60 * 1000);
    return d.toISOString().slice(0, 16);
  }
  return new Date(iso).toISOString().slice(0, 16);
}

// Thumbnail de vídeo: captura primeiro frame via canvas
function VideoThumb({ url, className }: { url: string; className: string }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [thumbUrl, setThumbUrl] = useState<string | null>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const handleLoaded = () => {
      video.currentTime = 0.5;
    };
    const handleSeeked = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext('2d')?.drawImage(video, 0, 0);
      setThumbUrl(canvas.toDataURL('image/jpeg', 0.8));
    };
    video.addEventListener('loadedmetadata', handleLoaded);
    video.addEventListener('seeked', handleSeeked);
    return () => {
      video.removeEventListener('loadedmetadata', handleLoaded);
      video.removeEventListener('seeked', handleSeeked);
    };
  }, [url]);

  return (
    <div className={`relative overflow-hidden ${className}`}>
      {thumbUrl ? (
        <img src={thumbUrl} alt="" className="h-full w-full object-cover" />
      ) : (
        <div className="flex h-full w-full items-center justify-center bg-slate-800 text-slate-500 text-2xl">▶</div>
      )}
      {/* play overlay */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="rounded-full bg-black/50 p-1">
          <svg className="h-4 w-4 text-white" fill="currentColor" viewBox="0 0 24 24">
            <path d="M8 5v14l11-7z" />
          </svg>
        </div>
      </div>
      {/* hidden elements for frame capture */}
      <video ref={videoRef} src={url} muted preload="metadata" crossOrigin="anonymous" className="hidden" />
      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
}

// Modal de edição para draft/scheduled
function EditModal({ post, onClose, onSaved }: { post: any; onClose: () => void; onSaved: () => void }) {
  const [content, setContent] = useState(post.content || '');
  const [scheduleMode, setScheduleMode] = useState<'now' | 'scheduled'>(
    post.status === 'scheduled' ? 'scheduled' : 'now'
  );
  const [scheduledFor, setScheduledFor] = useState(toLocalDatetimeValue(post.scheduledFor));
  const [saving, setSaving] = useState(false);

  const postId = post.latePostId || post._id;

  const handleSave = async () => {
    if (!content.trim()) { toast.error('Legenda não pode ser vazia'); return; }
    setSaving(true);
    try {
      const payload: any = { content };
      if (scheduleMode === 'now') {
        payload.publishNow = true;
      } else {
        payload.scheduledFor = new Date(scheduledFor).toISOString();
      }
      await (api as any).updatePost(postId, payload);
      toast.success('Post atualizado!');
      onSaved();
      onClose();
    } catch (err: any) {
      toast.error(err.message || 'Erro ao atualizar post');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div
        className="w-full max-w-lg rounded-2xl border border-slate-700 bg-slate-900 p-6 shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="mb-5 flex items-center justify-between">
          <h2 className="text-base font-bold text-slate-100">Editar post</h2>
          <button onClick={onClose} className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-800 hover:text-slate-200">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Legenda */}
        <label className="mb-1 block text-xs font-semibold text-slate-400">Legenda</label>
        <textarea
          value={content}
          onChange={e => setContent(e.target.value)}
          rows={5}
          className="mb-4 w-full resize-none rounded-xl border border-slate-700 bg-slate-800 px-3 py-2.5 text-sm text-slate-100 placeholder-slate-600 focus:border-indigo-500 focus:outline-none"
          placeholder="Legenda do post..."
        />

        {/* Modo publicação */}
        <label className="mb-2 block text-xs font-semibold text-slate-400">Publicação</label>
        <div className="mb-4 flex gap-2">
          {(['now', 'scheduled'] as const).map(m => (
            <button
              key={m}
              onClick={() => setScheduleMode(m)}
              className={`flex-1 rounded-xl border py-2 text-xs font-semibold transition-all ${
                scheduleMode === m
                  ? 'border-indigo-500 bg-indigo-600/20 text-indigo-300'
                  : 'border-slate-700 bg-slate-800 text-slate-500 hover:text-slate-300'
              }`}
            >
              {m === 'now' ? 'Publicar agora' : 'Agendar'}
            </button>
          ))}
        </div>

        {scheduleMode === 'scheduled' && (
          <div className="mb-4">
            <label className="mb-1 block text-xs font-semibold text-slate-400">Data e hora</label>
            <input
              type="datetime-local"
              value={scheduledFor}
              onChange={e => setScheduledFor(e.target.value)}
              className="w-full rounded-xl border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-slate-100 focus:border-indigo-500 focus:outline-none"
            />
          </div>
        )}

        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="rounded-xl border border-slate-700 px-4 py-2 text-sm text-slate-400 hover:text-slate-200">
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="rounded-xl bg-indigo-600 px-5 py-2 text-sm font-semibold text-white hover:bg-indigo-500 disabled:opacity-50"
          >
            {saving ? 'Salvando...' : scheduleMode === 'now' ? 'Publicar agora' : 'Salvar agendamento'}
          </button>
        </div>
      </div>
    </div>
  );
}

function PostCard({ post, onDelete, onEdit }: { post: any; onDelete: () => void; onEdit: () => void }) {
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!confirm('Apagar este post?')) return;
    setDeleting(true);
    try {
      await (api as any).request(`/posting/posts/${post.latePostId || post._id}`, { method: 'DELETE' });
      toast.success('Post apagado.');
      onDelete();
    } catch (err: any) {
      toast.error(err.message || 'Erro ao apagar post');
    } finally {
      setDeleting(false);
    }
  };

  const cfg = STATUS_CONFIG[post.status] || { label: post.status, cls: 'bg-slate-700 text-slate-300' };
  const media = post.mediaItems?.[0] || post.media?.[0];
  const isCarousel = (post.mediaItems || post.media || []).length > 1;
  const canEdit = ['draft', 'scheduled'].includes(post.status);

  return (
    <div className="group flex gap-4 rounded-2xl border border-slate-800 bg-slate-900 p-4 hover:border-slate-700 transition-all">
      {/* Thumbnail */}
      <div className="shrink-0">
        {media?.url && media.type === 'image' ? (
          <img
            src={media.url}
            alt=""
            className="h-20 w-14 rounded-xl object-cover border border-slate-700"
          />
        ) : media?.url && media.type === 'video' ? (
          <VideoThumb url={media.url} className="h-20 w-14 rounded-xl border border-slate-700" />
        ) : (
          <div className="flex h-20 w-14 items-center justify-center rounded-xl bg-slate-800 border border-slate-700 text-slate-600 text-xl">📄</div>
        )}
        {isCarousel && (
          <p className="mt-1 text-center text-[9px] text-slate-500">{(post.mediaItems || post.media || []).length} slides</p>
        )}
      </div>

      {/* Body */}
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2 mb-1.5">
          <p className="text-sm font-medium text-slate-200 line-clamp-2 leading-snug">
            {post.content || <span className="italic text-slate-600">Sem legenda</span>}
          </p>
          <div className="flex shrink-0 items-center gap-1.5">
            <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${cfg.cls}`}>{cfg.label}</span>
            {canEdit && (
              <button
                onClick={onEdit}
                className="hidden group-hover:flex h-6 w-6 items-center justify-center rounded-lg bg-slate-800 text-slate-500 hover:bg-indigo-900/40 hover:text-indigo-400 transition-colors text-xs"
                title="Editar"
              >✎</button>
            )}
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="hidden group-hover:flex h-6 w-6 items-center justify-center rounded-lg bg-slate-800 text-slate-500 hover:bg-red-900/40 hover:text-red-400 transition-colors text-xs"
            >✕</button>
          </div>
        </div>

        {/* Plataformas */}
        <div className="flex flex-wrap gap-1.5 mb-2">
          {(post.platforms || []).map((p: any, i: number) => {
            const pCfg = STATUS_CONFIG[p.status] || null;
            const colors = PLATFORM_COLORS[p.platform?.toLowerCase()] || { bg: 'bg-slate-700', text: 'text-white' };
            return (
              <div key={i} className="flex items-center gap-1.5 rounded-lg border border-slate-700 bg-slate-800 px-2 py-1">
                <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded ${colors.bg} ${colors.text}`}>
                  <PlatformIcon platform={p.platform} size={10} />
                </span>
                <span className="text-[10px] text-slate-400 max-w-[100px] truncate">
                  {p.accountUsername || p.platform}
                </span>
                {pCfg && (
                  <span className={`rounded-full px-1.5 text-[9px] font-semibold ${pCfg.cls}`}>{pCfg.label}</span>
                )}
                {p.platformPostUrl && (
                  <a href={p.platformPostUrl} target="_blank" rel="noopener noreferrer" className="text-[9px] text-indigo-400 hover:text-indigo-300">↗</a>
                )}
              </div>
            );
          })}
        </div>

        {/* Datas */}
        <div className="flex gap-4 text-[10px] text-slate-600">
          {post.scheduledFor && (
            <span>📅 Agendado: {formatDate(post.scheduledFor)}</span>
          )}
          {post.publishedAt && (
            <span>✓ Publicado: {formatDate(post.publishedAt)}</span>
          )}
          {!post.scheduledFor && !post.publishedAt && (
            <span>Criado: {formatDate(post.createdAt)}</span>
          )}
          {post.groupName && <span className="ml-auto">Perfil: {post.groupName}</span>}
        </div>
      </div>
    </div>
  );
}

export function SocialPostsListPage() {
  const [tab, setTab] = useState<PostStatus>('all');
  const [posts, setPosts] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [editingPost, setEditingPost] = useState<any | null>(null);
  const LIMIT = 20;

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(LIMIT),
        ...(tab !== 'all' ? { status: tab } : {}),
      });
      const res = await (api as any).request(`/posting/posts?${params}`);
      if (res.success) {
        setPosts(res.data?.posts || []);
        setTotalPages(res.data?.pagination?.pages || 1);
        setTotal(res.data?.pagination?.total || 0);
      }
    } catch (err: any) {
      toast.error('Erro ao carregar postagens');
    } finally {
      setLoading(false);
    }
  }, [tab, page]);

  useEffect(() => {
    setPage(1);
  }, [tab]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 p-6">
      <div className="mx-auto max-w-4xl space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-slate-100">Postagens</h1>
            <p className="text-xs text-slate-500 mt-0.5">{total} post{total !== 1 ? 's' : ''} encontrado{total !== 1 ? 's' : ''}</p>
          </div>
          <button
            onClick={fetchPosts}
            className="rounded-xl border border-slate-700 px-3 py-1.5 text-sm text-slate-400 hover:text-slate-200 hover:border-slate-600 transition-colors"
          >
            ↻ Atualizar
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 rounded-xl bg-slate-900 border border-slate-800 p-1">
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex-1 rounded-lg py-1.5 text-xs font-semibold transition-all ${
                tab === t.id
                  ? 'bg-indigo-600 text-white'
                  : 'text-slate-500 hover:text-slate-300'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Posts */}
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-28 rounded-2xl bg-slate-900 border border-slate-800 animate-pulse" />
            ))}
          </div>
        ) : posts.length === 0 ? (
          <div className="rounded-2xl border border-slate-800 bg-slate-900 py-16 text-center">
            <p className="text-slate-500 font-medium">Nenhum post encontrado</p>
            <p className="text-slate-700 text-sm mt-1">
              {tab === 'all' ? 'Crie posts via Marketing Studio.' : `Nenhum post com status "${tab}".`}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {posts.map((post: any) => (
              <PostCard
                key={post._id || post.latePostId}
                post={post}
                onDelete={fetchPosts}
                onEdit={() => setEditingPost(post)}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-3 pt-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="rounded-lg border border-slate-700 px-3 py-1.5 text-sm text-slate-400 hover:text-slate-200 disabled:opacity-30"
            >
              ← Anterior
            </button>
            <span className="text-xs text-slate-500">Página {page} de {totalPages}</span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="rounded-lg border border-slate-700 px-3 py-1.5 text-sm text-slate-400 hover:text-slate-200 disabled:opacity-30"
            >
              Próxima →
            </button>
          </div>
        )}
      </div>

      {/* Edit modal */}
      {editingPost && (
        <EditModal
          post={editingPost}
          onClose={() => setEditingPost(null)}
          onSaved={fetchPosts}
        />
      )}
    </div>
  );
}
